import * as React from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';

import { cn } from '@/shared/infra/ui/lib/utils';

const Popover = PopoverPrimitive.Root;

const PopoverTrigger = PopoverPrimitive.Trigger;

const PopoverAnchor = PopoverPrimitive.Anchor;

/**
 * Keep the panel clear of the viewport edge. Radix flips and shifts a popover to stay on screen, but with
 * zero padding "on screen" means flush against the edge — which is what let a calendar sit half over the
 * page chrome. It also can't help at all if the content is simply wider than the space available, hence
 * the max-width below.
 */
const COLLISION_PADDING = 8;

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content>
>(({ className, align = 'center', sideOffset = 4, collisionPadding, ...props }, ref) => (
  <PopoverPrimitive.Portal>
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      collisionPadding={collisionPadding ?? COLLISION_PADDING}
      className={cn(
        'z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
        // Never wider or taller than the space Radix measured, so content can't push the panel off-screen
        // no matter what a caller puts inside it. `--radix-popover-content-available-*` are set by Radix
        // from the collision boundary. A caller's own w-*/max-* class still wins (it comes after).
        'max-w-[calc(100vw-1rem)] sm:max-w-[var(--radix-popover-content-available-width)]',
        'max-h-[var(--radix-popover-content-available-height)] overflow-y-auto',
        className
      )}
      {...props}
    />
  </PopoverPrimitive.Portal>
));
PopoverContent.displayName = PopoverPrimitive.Content.displayName;

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor };
